import type { Camera } from '../render/camera';
import type { Worm } from '../entities/worm';
import type { Projectile } from '../entities/projectile';
import type { Sheep } from '../weapons/sheep';
import type { SuperSheep } from '../weapons/superSheep';
import type { MiniRaon } from '../weapons/raon';
import type { HomingMissile } from '../weapons/homingMissile';
import type { Crate } from '../entities/crate';
import { WA_PHYSICS } from './constants';

export type CameraMode =
  | 'worm'
  | 'projectile'
  | 'manual'
  | 'thrown'
  | 'crate'
  | 'homing'
  | 'resolution'
  | 'sheep'
  | 'supersheep'
  | 'raon';

export interface CameraPanInput {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
}

export interface TrackedEntities {
  projectile: Projectile | null;
  homingMissile: HomingMissile | null;
  thrownWorm: Worm | null;
  sheep: Sheep | null;
  superSheep: SuperSheep | null;
  raon: MiniRaon | null;
  resolvingWorm: Worm | null;
  crate: Crate | null;
}

export interface TargetingState {
  active: boolean;
  withDpad: boolean;
  cursorPos: { x: number; y: number };
}

export class CameraController {
  private mode: CameraMode = 'worm';
  private transitionDelay = 0;
  private manualOffset = { x: 0, y: 0 };

  private readonly CAMERA_PAN_SPEED = 400;
  private readonly CAMERA_TRANSITION_DELAY = 0.4;

  constructor(private camera: Camera) {}

  getMode(): CameraMode {
    return this.mode;
  }

  setMode(mode: CameraMode): void {
    this.mode = mode;
  }

  resetManualOffset(): void {
    this.manualOffset = { x: 0, y: 0 };
  }

  setTransitionDelay(delay: number): void {
    this.transitionDelay = delay;
  }

  updatePan(deltaTime: number, panInput: CameraPanInput): void {
    const { left, right, up, down } = panInput;
    if (!left && !right && !up && !down) return;

    const isTrackingProjectile = this.mode === 'projectile' || this.mode === 'homing';
    if (!isTrackingProjectile) {
      this.mode = 'manual';
    }
    const speed = this.CAMERA_PAN_SPEED * deltaTime;

    if (left) this.camera.pan(-speed, 0);
    if (right) this.camera.pan(speed, 0);
    if (up) this.camera.pan(0, -speed);
    if (down) this.camera.pan(0, speed);
  }

  updateTarget(
    deltaTime: number,
    currentWorm: Worm | null,
    tracked: TrackedEntities,
    targeting: TargetingState,
  ): void {
    const camOffset = WA_PHYSICS.WORLD.CAMERA_PADDING;

    if (tracked.projectile && !tracked.projectile.active) {
      const lastPos = { x: tracked.projectile.position.x, y: tracked.projectile.position.y };
      tracked.projectile = null;
      if (this.mode === 'projectile') {
        this.camera.setTarget(lastPos.x + camOffset, lastPos.y);
        if (tracked.thrownWorm) {
          this.mode = 'thrown';
          this.transitionDelay = this.CAMERA_TRANSITION_DELAY;
        } else {
          this.mode = 'manual';
        }
      }
    }

    if (tracked.homingMissile && !tracked.homingMissile.active) {
      const lastPos = { x: tracked.homingMissile.position.x, y: tracked.homingMissile.position.y };
      tracked.homingMissile = null;
      if (this.mode === 'homing') {
        this.camera.setTarget(lastPos.x + camOffset, lastPos.y);
        if (tracked.thrownWorm) {
          this.mode = 'thrown';
          this.transitionDelay = this.CAMERA_TRANSITION_DELAY;
        } else {
          this.mode = 'manual';
        }
      }
    }

    if (tracked.thrownWorm && this.mode === 'thrown') {
      const isStable =
        tracked.thrownWorm.isGrounded &&
        Math.abs(tracked.thrownWorm.velocity.x) < 5 &&
        Math.abs(tracked.thrownWorm.velocity.y) < 5;

      if (isStable || tracked.thrownWorm.state === 'dead') {
        tracked.thrownWorm = null;
        this.mode = 'manual';
      }
    }

    if (this.transitionDelay > 0) {
      this.transitionDelay -= deltaTime;
      return;
    }

    if (this.mode === 'projectile' && tracked.projectile) {
      this.camera.setTarget(
        tracked.projectile.position.x + camOffset,
        tracked.projectile.position.y,
      );
    } else if (this.mode === 'homing' && tracked.homingMissile) {
      this.camera.setTarget(
        tracked.homingMissile.position.x + camOffset,
        tracked.homingMissile.position.y,
      );
    } else if (this.mode === 'thrown' && tracked.thrownWorm) {
      this.camera.setTarget(
        tracked.thrownWorm.position.x + camOffset,
        tracked.thrownWorm.position.y,
      );
    } else if (this.mode === 'sheep' && tracked.sheep) {
      this.camera.setTarget(tracked.sheep.position.x + 8 + camOffset, tracked.sheep.position.y + 6);
    } else if (this.mode === 'supersheep' && tracked.superSheep) {
      this.camera.setTarget(
        tracked.superSheep.position.x + 8 + camOffset,
        tracked.superSheep.position.y + 6,
      );
    } else if (this.mode === 'raon' && tracked.raon) {
      this.camera.setTarget(tracked.raon.position.x + 8 + camOffset, tracked.raon.position.y + 7);
    } else if (this.mode === 'resolution' && tracked.resolvingWorm) {
      this.camera.setTarget(
        tracked.resolvingWorm.position.x + camOffset,
        tracked.resolvingWorm.position.y,
      );
    } else if (this.mode === 'crate' && tracked.crate) {
      this.camera.setTarget(tracked.crate.position.x + camOffset, tracked.crate.position.y);
    } else if (targeting.active && targeting.withDpad) {
      this.camera.setTarget(targeting.cursorPos.x + camOffset, targeting.cursorPos.y);
    } else if (this.mode === 'worm' && currentWorm) {
      this.camera.setTarget(
        currentWorm.position.x + this.manualOffset.x + camOffset,
        currentWorm.position.y + this.manualOffset.y,
      );
    }
  }

  focusWorm(worm: Worm): void {
    this.camera.setTarget(worm.position.x + WA_PHYSICS.WORLD.CAMERA_PADDING, worm.position.y);
  }
}
